import { useTranslation } from "react-i18next";
import { ScrollText } from "lucide-react";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { buildReportMessagesExportPath } from "../../api/reports-api.js";
import { isApiError } from "../../api/client.js";
import { ErrorState } from "../../components/common/ErrorState/index.js";
import { messageLogEmptyKind } from "./report-empty-kind.js";
import { MESSAGE_LOG_COLUMNS, type MessageLogColumnId } from "./report-column-defs.js";
import { MessageDetailDrawer } from "./MessageDetailDrawer.js";
import { MessageLogTable } from "./MessageLogTable.js";
import { MessageLogToolbar } from "./MessageLogToolbar.js";
import { ReportColumnsPopover } from "./ReportColumnsPopover.js";
import { ReportExportMenu } from "./ReportExportMenu.js";
import { ReportPageChrome } from "./ReportPageChrome.js";
import { ReportPageHeader } from "./ReportPageHeader.js";
import { ReportRefreshButton } from "./ReportRefreshButton.js";
import { useMessageLogReport } from "./use-message-log-report.js";
import { useReportMessageDetail } from "./use-report-message-detail.js";
import { useReportTableLayout } from "./use-report-table-layout.js";
import { exportLocaleFromUi, useReportExport } from "./use-report-export.js";
import type { MessageLogFilterValues } from "./MessageLogFilters.js";

function messageLogColumnLabel(t: (key: string) => string, id: MessageLogColumnId): string {
  switch (id) {
    case "party":
      return t("reports.messageLog.columns.contact");
    case "platform":
      return t("reports.messageLog.columns.channel");
    default:
      return t(`reports.messageLog.columns.${id}`);
  }
}

export function MessageLogPage({
  organizationId,
  role,
}: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const report = useMessageLogReport({ organizationId, role });
  const detail = useReportMessageDetail({
    organizationId,
    canAccess: report.canAccess,
  });
  const exportCtl = useReportExport();
  const layout = useReportTableLayout(MESSAGE_LOG_COLUMNS);

  const onFiltersChange = (patch: Partial<MessageLogFilterValues>) => {
    if ("direction" in patch) report.setDirection(patch.direction);
    if ("platform" in patch) report.setPlatform(patch.platform);
    if ("endpointId" in patch) report.setEndpointId(patch.endpointId);
    if ("messageType" in patch) report.setMessageType(patch.messageType);
    if ("status" in patch) report.setStatus(patch.status);
    if ("origin" in patch) report.setOrigin(patch.origin);
    if ("campaignId" in patch) report.setCampaignId(patch.campaignId);
    if ("template" in patch) report.setTemplate(patch.template);
  };

  const onExport = (format: "csv" | "xlsx") => {
    const path = buildReportMessagesExportPath(organizationId, {
      direction: report.query.direction,
      fromDate: report.query.from,
      toDate: report.query.to,
      search: report.query.search || undefined,
      platform: report.query.platform,
      endpointId: report.query.endpointId,
      messageType: report.query.messageType,
      status: report.query.status,
      origin: report.query.origin,
      campaignId: report.query.campaignId,
      template: report.query.template,
      sort: report.query.sort,
      format,
      locale: exportLocaleFromUi(i18n.language),
    });
    void exportCtl.runExport({
      pathWithQuery: path,
      fallbackFilename: `message-log.${format}`,
    });
  };

  const errorMessage =
    report.error == null
      ? null
      : isApiError(report.error) && report.error.status === 0
        ? t("reports.messageLog.backendUnavailable")
        : isApiError(report.error) && report.error.status === 403
          ? t("reports.messageLog.forbidden")
          : t("reports.messageLog.loadFailed");

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden"
      data-testid="reports-message-log-page"
    >
      <ReportPageChrome>
        <ReportPageHeader
          icon={ScrollText}
          title={t("reports.messageLog.title")}
          description={t("reports.messageLog.helper")}
          actions={
            report.canAccess ? (
              <div className="flex items-center gap-2">
                <ReportRefreshButton
                  refreshing={Boolean(report.loading && report.list)}
                  disabled={report.loading}
                  onRefresh={() => {
                    report.refresh();
                    if (detail.messageId != null) detail.refresh();
                  }}
                  testId="reports-message-log-refresh"
                />
                <div className="grid gap-1">
                  <ReportExportMenu
                    exporting={exportCtl.exporting}
                    onExport={onExport}
                    testId="reports-message-log-export"
                  />
                  {exportCtl.exportError ? (
                    <p className="m-0 text-[0.75rem] text-danger" role="alert">
                      {exportCtl.exportError}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null
          }
        />

        <MessageLogToolbar
          query={report.query}
          dateFrom={report.dateFrom}
          dateTo={report.dateTo}
          options={report.options}
          filteredTotal={report.list?.totalCount ?? null}
          filtersActive={report.filtersActive}
          onSearchChange={report.setSearch}
          onDateRangeChange={report.setDateRange}
          onFiltersChange={onFiltersChange}
          onResetFilters={report.resetFilters}
          columnsSlot={
            <ReportColumnsPopover
              columnOrder={layout.columnOrder}
              hiddenColumns={layout.hiddenColumns}
              requiredColumns={MESSAGE_LOG_COLUMNS.requiredColumns}
              labelFor={(id) => messageLogColumnLabel(t, id)}
              onToggle={layout.toggleColumn}
              onMove={layout.moveColumn}
              onReorder={layout.reorderColumn}
              onReset={layout.resetColumns}
              testIdPrefix="reports-message-log"
            />
          }
        />
      </ReportPageChrome>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-3 md:overflow-hidden md:px-5">
        {errorMessage ? (
          <ErrorState
            layout="panel"
            message={errorMessage}
            onRetry={report.refresh}
            retryLabel={t("auth.retry")}
          />
        ) : null}

        <div className="flex min-h-[20rem] min-h-0 flex-1 flex-col md:min-h-0">
          <MessageLogTable
            list={report.list}
            loading={report.loading}
            page={report.query.page}
            pageSize={report.query.pageSize}
            emptyKind={messageLogEmptyKind(report.query)}
            visibleColumns={layout.visibleColumns}
            widths={layout.widths}
            onResizeColumn={layout.resizeColumn}
            onReorderColumn={layout.reorderColumn}
            onPageChange={report.setPage}
            onPageSizeChange={report.setPageSize}
            onResetFilters={report.resetFilters}
            onOpenMessage={detail.openMessage}
          />
        </div>
      </div>

      <MessageDetailDrawer
        open={detail.messageId != null}
        detail={detail.detail}
        loading={detail.loading}
        error={detail.error}
        onClose={detail.closeMessage}
        onRetry={detail.refresh}
      />
    </div>
  );
}
